import { useCallback, useState } from 'react'
import { lumi } from '@/lib/lumi'

/**
 * Contenu SEO éditable (texte + photo + meta) pour les pages CVthèque
 * filtrées par qualification et région (ex: /disponibles/bnssa/bretagne).
 */
export interface CvthequeContentPage {
  _id: string
  pageKey: string
  qualifSlug: string
  regionSlug: string
  keyword: string
  metaTitle: string
  metaDescription: string
  heroImage: string
  introHtml: string
  bodyHtml: string
  isActive?: boolean
  creator: string
  createdAt: string
  updatedAt: string
}

export interface CvthequeContentPageInput {
  pageKey: string
  qualifSlug: string
  regionSlug: string
  keyword: string
  metaTitle: string
  metaDescription: string
  heroImage: string
  introHtml: string
  bodyHtml: string
  isActive?: boolean
}

/** Construit la clé unique d'une page à partir des slugs qualification/région. */
export function buildCvthequeContentKey(qualifSlug?: string, regionSlug?: string): string {
  if (!qualifSlug || !regionSlug) return ''
  return `${qualifSlug}-${regionSlug}`
}

export function useCvthequeContentPages() {
  const [items, setItems] = useState<CvthequeContentPage[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /** Récupère toutes les pages de contenu (usage admin). */
  const fetchAll = useCallback(async (): Promise<CvthequeContentPage[]> => {
    setLoading(true)
    setError(null)
    try {
      const { list } = await lumi.entities.cvtheque_content_pages.list({
        sort: { updatedAt: -1 },
        limit: 200,
      })
      const arr = (list as CvthequeContentPage[]) || []
      setItems(arr)
      return arr
    } catch (e) {
      console.error('[cvtheque_content_pages] fetchAll failed:', e)
      setError('Impossible de charger le contenu SEO de la CVthèque.')
      return []
    } finally {
      setLoading(false)
    }
  }, [])

  /** Récupère une page de contenu par sa clé unique (usage public, front). */
  const fetchByKey = useCallback(
    async (pageKey: string): Promise<CvthequeContentPage | null> => {
      if (!pageKey) return null
      try {
        const { list } = await lumi.entities.cvtheque_content_pages.list({
          filter: { pageKey },
          limit: 1,
        })
        const arr = list as CvthequeContentPage[]
        return arr.length > 0 ? arr[0] : null
      } catch (e) {
        console.error('[cvtheque_content_pages] fetchByKey failed:', e)
        return null
      }
    },
    []
  )

  /** [Admin] Crée une nouvelle page de contenu. */
  const create = useCallback(
    async (data: CvthequeContentPageInput, creator: string): Promise<CvthequeContentPage> => {
      setSaving(true)
      setError(null)
      try {
        const now = new Date().toISOString()
        const created = await lumi.entities.cvtheque_content_pages.create({
          ...data,
          isActive: data.isActive !== false,
          creator,
          createdAt: now,
          updatedAt: now,
        })
        console.log('[cvtheque_content_pages] create OK')
        setItems((prev) => [created as CvthequeContentPage, ...prev])
        return created as CvthequeContentPage
      } catch (e) {
        console.error('[cvtheque_content_pages] create failed:', e)
        setError('Impossible de créer cette page de contenu.')
        throw e
      } finally {
        setSaving(false)
      }
    },
    []
  )

  /** [Admin] Met à jour une page de contenu existante. */
  const update = useCallback(
    async (id: string, patch: Partial<CvthequeContentPageInput>): Promise<CvthequeContentPage> => {
      setSaving(true)
      setError(null)
      try {
        const payload = { ...patch, updatedAt: new Date().toISOString() }
        const updated = await lumi.entities.cvtheque_content_pages.update(id, payload)
        console.log('[cvtheque_content_pages] update OK', id)
        setItems((prev) =>
          prev.map((it) => (it._id === id ? ({ ...it, ...payload } as CvthequeContentPage) : it))
        )
        return updated as CvthequeContentPage
      } catch (e) {
        console.error('[cvtheque_content_pages] update failed:', e)
        setError('Impossible de mettre à jour cette page de contenu.')
        throw e
      } finally {
        setSaving(false)
      }
    },
    []
  )

  /** [Admin] Supprime une page de contenu. */
  const remove = useCallback(async (id: string): Promise<void> => {
    try {
      await lumi.entities.cvtheque_content_pages.delete(id)
      console.log('[cvtheque_content_pages] delete OK', id)
      setItems((prev) => prev.filter((it) => it._id !== id))
    } catch (e) {
      console.error('[cvtheque_content_pages] delete failed:', e)
      throw e
    }
  }, [])

  return { items, loading, saving, error, fetchAll, fetchByKey, create, update, remove }
}
